import React from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  VideoLibrary as ResourceIcon,
} from '@mui/icons-material';
import { KnowledgeNode } from '../../types/knowledge';

interface NodeContextMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  node: KnowledgeNode | null;
  onAddConnection: (node: KnowledgeNode) => void;
  onEditNode: (node: KnowledgeNode) => void;
  onDeleteNode: (node: KnowledgeNode) => void;
  onViewArticle: (node: KnowledgeNode) => void;
  onAddArticle: (node: KnowledgeNode) => void;
  onAddResource: (node: KnowledgeNode) => void;
}

export function NodeContextMenu({
  anchorEl,
  open,
  onClose,
  node,
  onAddConnection,
  onEditNode,
  onDeleteNode,
  onViewArticle,
  onAddArticle,
  onAddResource,
}: NodeContextMenuProps) {
  if (!node) return null;

  const handleMenuAction = (action: () => void) => {
    action();
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      PaperProps={{
        sx: {
          minWidth: 200,
        },
      }}
    >
      <MenuItem disabled>
        <ListItemText>
          <Typography variant="subtitle2" color="text.secondary">
            {node.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {node.type}
          </Typography>
        </ListItemText>
      </MenuItem>
      
      <Divider />
      
      <MenuItem onClick={() => handleMenuAction(() => onAddConnection(node))}>
        <ListItemIcon>
          <LinkIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Create Connection</ListItemText>
      </MenuItem>
      
      <MenuItem onClick={() => handleMenuAction(() => onViewArticle(node))}>
        <ListItemIcon>
          <ViewIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>View Article</ListItemText>
      </MenuItem>
      
      <MenuItem onClick={() => handleMenuAction(() => onAddArticle(node))}>
        <ListItemIcon>
          <AddIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Add Article</ListItemText>
      </MenuItem>
      
      <MenuItem onClick={() => handleMenuAction(() => onAddResource(node))}>
        <ListItemIcon>
          <ResourceIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Add Resource</ListItemText>
      </MenuItem>
      
      <Divider />
      
      <MenuItem onClick={() => handleMenuAction(() => onEditNode(node))}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit Node</ListItemText>
      </MenuItem>
      
      <MenuItem 
        onClick={() => handleMenuAction(() => onDeleteNode(node))}
        sx={{ color: 'error.main' }}
      >
        <ListItemIcon>
          <DeleteIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText>Delete Node</ListItemText>
      </MenuItem>
    </Menu>
  );
}
